import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Edit2,
  Plus,
  Star,
  StarOff,
  Trash2,
  Wallet,
} from 'lucide-react';

import {
  apiClient,
  type ApiError,
  type BankAccount,
  type BankAccountType,
  type CostCenter,
  type FinancialCategory,
} from '@/lib/api-client';
import { cn } from '@/lib/utils';

type Tab = 'accounts' | 'categories' | 'cost-centers';

const ACCOUNT_TYPE_LABEL: Record<BankAccountType, string> = {
  conta_corrente: 'Conta corrente',
  conta_poupanca: 'Conta poupança',
  cartao_credito: 'Cartão de crédito',
  dinheiro: 'Dinheiro',
  asaas: 'Asaas',
  investimento: 'Investimento',
  outros: 'Outros',
};

export function FinancialConfigPage() {
  const [tab, setTab] = useState<Tab>('accounts');

  return (
    <div className="p-6 space-y-4">
      <header>
        <h1 className="text-2xl font-semibold text-[var(--fg)]">Financeiro</h1>
        <p className="text-sm text-[var(--fg-muted)] mt-1">
          Configure a estrutura interna de acordo com a realidade do seu
          escritório.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-[var(--border)]">
        <TabButton
          active={tab === 'accounts'}
          onClick={() => setTab('accounts')}
          label="Contas e cartões"
        />
        <TabButton
          active={tab === 'categories'}
          onClick={() => setTab('categories')}
          label="Categorias"
        />
        <TabButton
          active={tab === 'cost-centers'}
          onClick={() => setTab('cost-centers')}
          label="Centros de custos"
        />
      </nav>

      {tab === 'accounts' && <AccountsTab />}
      {tab === 'categories' && <CategoriesTab />}
      {tab === 'cost-centers' && <CostCentersTab />}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-medium border-b-2 transition',
        active
          ? 'border-[var(--accent)] text-[var(--fg)]'
          : 'border-transparent text-[var(--fg-muted)] hover:text-[var(--fg)]',
      )}
    >
      {label}
    </button>
  );
}

// ===========================================================================
// Contas
// ===========================================================================

interface AccountForm {
  name: string;
  type: BankAccountType;
  bankInstitution: string;
  initialBalance: string;
  isDefault: boolean;
  active: boolean;
}

function AccountsTab() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [form, setForm] = useState<AccountForm>({
    name: '',
    type: 'conta_corrente',
    bankInstitution: '',
    initialBalance: '0',
    isDefault: false,
    active: true,
  });
  const [error, setError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['financial', 'accounts'],
    queryFn: () => apiClient.bankAccountsList(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      apiClient.bankAccountsCreate({
        name: form.name,
        type: form.type,
        bankInstitution: form.bankInstitution || null,
        initialBalance: Number(form.initialBalance) || 0,
        isDefault: form.isDefault,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial', 'accounts'] });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const updateMut = useMutation({
    mutationFn: () =>
      apiClient.bankAccountsUpdate(editing!.id, {
        name: form.name,
        type: form.type,
        bankInstitution: form.bankInstitution || null,
        initialBalance: Number(form.initialBalance) || 0,
        isDefault: form.isDefault,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial', 'accounts'] });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.bankAccountsDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial', 'accounts'] });
    },
  });
  const setDefaultMut = useMutation({
    mutationFn: (id: string) => apiClient.bankAccountsSetDefault(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['financial', 'accounts'] });
    },
  });

  function openCreate() {
    setEditing(null);
    setForm({
      name: '',
      type: 'conta_corrente',
      bankInstitution: '',
      initialBalance: '0',
      isDefault: false,
      active: true,
    });
    setError(null);
    setModalOpen(true);
  }

  function openEdit(acc: BankAccount) {
    setEditing(acc);
    setForm({
      name: acc.name,
      type: acc.type,
      bankInstitution: acc.bankInstitution ?? '',
      initialBalance: String(acc.initialBalance),
      isDefault: acc.isDefault,
      active: acc.active,
    });
    setError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (editing) updateMut.mutate();
    else createMut.mutate();
  }

  const items = listQ.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <p className="text-sm text-[var(--fg-muted)]">
          {items.length} conta(s) cadastrada(s)
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Nova conta
        </button>
      </div>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-[var(--fg-muted)] text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Tipo</th>
              <th className="px-3 py-2 text-left">Instituição</th>
              <th className="px-3 py-2 text-right">Saldo</th>
              <th className="px-3 py-2 text-center">Padrão</th>
              <th className="px-3 py-2 text-center">Ativa</th>
              <th className="px-3 py-2 w-20" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-[var(--fg-muted)]"
                >
                  Nenhuma conta cadastrada.
                </td>
              </tr>
            )}
            {items.map((acc) => (
              <tr
                key={acc.id}
                className="border-t border-[var(--card-border)] hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]"
              >
                <td className="px-3 py-2 text-[var(--fg)]">
                  <Wallet className="inline h-3 w-3 mr-1 text-[var(--fg-muted)]" />
                  {acc.name}
                </td>
                <td className="px-3 py-2 text-[var(--fg-muted)]">
                  {ACCOUNT_TYPE_LABEL[acc.type]}
                </td>
                <td className="px-3 py-2 text-[var(--fg-muted)]">
                  {acc.bankInstitution ?? '—'}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {acc.balance.toLocaleString('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                  })}
                </td>
                <td className="px-3 py-2 text-center">
                  <button
                    type="button"
                    onClick={() => setDefaultMut.mutate(acc.id)}
                    disabled={acc.isDefault}
                    title={acc.isDefault ? 'Padrão' : 'Tornar padrão'}
                    className="text-amber-500 disabled:cursor-default"
                  >
                    {acc.isDefault ? (
                      <Star className="h-4 w-4 fill-current" />
                    ) : (
                      <StarOff className="h-4 w-4 text-[var(--fg-muted)]" />
                    )}
                  </button>
                </td>
                <td className="px-3 py-2 text-center">
                  {acc.active ? (
                    <span className="text-emerald-600">Sim</span>
                  ) : (
                    <span className="text-[var(--fg-muted)]">Não</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(acc)}
                    className="text-[var(--fg-muted)] hover:text-[var(--fg)] mr-2"
                  >
                    <Edit2 className="h-4 w-4 inline" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Excluir conta "${acc.name}"?`))
                        deleteMut.mutate(acc.id);
                    }}
                    className="text-[var(--fg-muted)] hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <Modal title={editing ? 'Editar conta' : 'Nova conta'} onClose={closeModal}>
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <Field label="Nome" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
                required
              />
            </Field>
            <Field label="Tipo" required>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    type: e.target.value as BankAccountType,
                  }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
              >
                {Object.entries(ACCOUNT_TYPE_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Instituição financeira">
              <input
                type="text"
                value={form.bankInstitution}
                onChange={(e) =>
                  setForm((f) => ({ ...f, bankInstitution: e.target.value }))
                }
                placeholder="Ex: Nubank, Itaú"
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
              />
            </Field>
            <Field label="Saldo inicial">
              <input
                type="number"
                step="0.01"
                value={form.initialBalance}
                onChange={(e) =>
                  setForm((f) => ({ ...f, initialBalance: e.target.value }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm tabular-nums"
              />
            </Field>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.isDefault}
                onChange={(e) =>
                  setForm((f) => ({ ...f, isDefault: e.target.checked }))
                }
              />
              Conta padrão
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, active: e.target.checked }))
                }
              />
              Ativa
            </label>
            <ModalFooter
              onCancel={closeModal}
              loading={createMut.isPending || updateMut.isPending}
            />
          </form>
        </Modal>
      )}
    </div>
  );
}

// ===========================================================================
// Categorias
// ===========================================================================

interface CategoryForm {
  name: string;
  type: 'income' | 'expense';
  parentId: string;
  color: string;
  active: boolean;
}

function CategoriesTab() {
  const queryClient = useQueryClient();
  const [tabType, setTabType] = useState<'income' | 'expense'>('income');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<FinancialCategory | null>(null);
  const [form, setForm] = useState<CategoryForm>({
    name: '',
    type: 'income',
    parentId: '',
    color: '#10B981',
    active: true,
  });
  const [error, setError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['financial', 'categories', 'list', tabType],
    queryFn: () => apiClient.financialCategoriesList({ type: tabType }),
  });

  const createMut = useMutation({
    mutationFn: () =>
      apiClient.financialCategoriesCreate({
        name: form.name,
        type: form.type,
        parentId: form.parentId || null,
        color: form.color,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'categories'],
      });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const updateMut = useMutation({
    mutationFn: () =>
      apiClient.financialCategoriesUpdate(editing!.id, {
        name: form.name,
        type: form.type,
        parentId: form.parentId || null,
        color: form.color,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'categories'],
      });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.financialCategoriesDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'categories'],
      });
    },
  });

  function openCreate(parent?: FinancialCategory) {
    setEditing(null);
    setForm({
      name: '',
      type: tabType,
      parentId: parent?.id ?? '',
      color: '#10B981',
      active: true,
    });
    setError(null);
    setModalOpen(true);
  }

  function openEdit(cat: FinancialCategory) {
    setEditing(cat);
    setForm({
      name: cat.name,
      type: cat.type,
      parentId: cat.parentId ?? '',
      color: cat.color,
      active: cat.active,
    });
    setError(null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (editing) updateMut.mutate();
    else createMut.mutate();
  }

  const items = listQ.data ?? [];
  const parents = items.filter((c) => !c.parentId);
  const childrenByParent = new Map<string, FinancialCategory[]>();
  for (const c of items) {
    if (c.parentId) {
      const arr = childrenByParent.get(c.parentId) ?? [];
      arr.push(c);
      childrenByParent.set(c.parentId, arr);
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div className="flex gap-1">
          <SubTab
            active={tabType === 'income'}
            onClick={() => setTabType('income')}
            label="Receitas"
          />
          <SubTab
            active={tabType === 'expense'}
            onClick={() => setTabType('expense')}
            label="Despesas"
          />
        </div>
        <button
          type="button"
          onClick={() => openCreate()}
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Nova categoria
        </button>
      </div>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg overflow-hidden">
        {parents.length === 0 && (
          <p className="px-3 py-8 text-center text-sm text-[var(--fg-muted)]">
            Nenhuma categoria cadastrada.
          </p>
        )}
        {parents.map((parent) => (
          <div
            key={parent.id}
            className="border-b border-[var(--card-border)] last:border-b-0"
          >
            <CategoryRow
              cat={parent}
              indent={0}
              onEdit={openEdit}
              onDelete={(id) => {
                if (confirm(`Excluir categoria "${parent.name}"?`))
                  deleteMut.mutate(id);
              }}
              onAddChild={() => openCreate(parent)}
            />
            {(childrenByParent.get(parent.id) ?? []).map((child) => (
              <CategoryRow
                key={child.id}
                cat={child}
                indent={1}
                onEdit={openEdit}
                onDelete={(id) => {
                  if (confirm(`Excluir categoria "${child.name}"?`))
                    deleteMut.mutate(id);
                }}
              />
            ))}
          </div>
        ))}
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Editar categoria' : 'Nova categoria'}
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <Field label="Nome" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
                required
              />
            </Field>
            <Field label="Tipo" required>
              <select
                value={form.type}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    type: e.target.value as 'income' | 'expense',
                  }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
              >
                <option value="income">Receita</option>
                <option value="expense">Despesa</option>
              </select>
            </Field>
            <Field label="Categoria pai (opcional)">
              <select
                value={form.parentId}
                onChange={(e) =>
                  setForm((f) => ({ ...f, parentId: e.target.value }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
              >
                <option value="">Categoria raiz</option>
                {parents
                  .filter((p) => p.id !== editing?.id)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Field label="Cor">
              <input
                type="color"
                value={form.color}
                onChange={(e) =>
                  setForm((f) => ({ ...f, color: e.target.value }))
                }
                className="h-9 w-20 rounded border border-[var(--border)]"
              />
            </Field>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, active: e.target.checked }))
                }
              />
              Ativa
            </label>
            <ModalFooter
              onCancel={closeModal}
              loading={createMut.isPending || updateMut.isPending}
            />
          </form>
        </Modal>
      )}
    </div>
  );
}

function CategoryRow({
  cat,
  indent,
  onEdit,
  onDelete,
  onAddChild,
}: {
  cat: FinancialCategory;
  indent: number;
  onEdit: (c: FinancialCategory) => void;
  onDelete: (id: string) => void;
  onAddChild?: () => void;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]',
        indent > 0 && 'pl-10 border-t border-[var(--card-border)]',
      )}
    >
      <span
        className="inline-block h-3 w-3 rounded-full"
        style={{ backgroundColor: cat.color }}
      />
      <span className="flex-1 text-sm text-[var(--fg)]">{cat.name}</span>
      {!cat.active && (
        <span className="text-xs text-[var(--fg-muted)]">(inativa)</span>
      )}
      {onAddChild && (
        <button
          type="button"
          onClick={onAddChild}
          className="text-xs text-[var(--fg-muted)] hover:text-[var(--fg)]"
          title="Adicionar subcategoria"
        >
          + filho
        </button>
      )}
      <button
        type="button"
        onClick={() => onEdit(cat)}
        className="text-[var(--fg-muted)] hover:text-[var(--fg)]"
      >
        <Edit2 className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onDelete(cat.id)}
        className="text-[var(--fg-muted)] hover:text-red-600"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  );
}

function SubTab({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'px-3 py-1.5 text-sm rounded-md',
        active
          ? 'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]'
          : 'text-[var(--fg-muted)] hover:text-[var(--fg)]',
      )}
    >
      {label}
    </button>
  );
}

// ===========================================================================
// Centros de custo
// ===========================================================================

function CostCentersTab() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CostCenter | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    active: true,
  });
  const [error, setError] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['financial', 'cost-centers'],
    queryFn: () => apiClient.costCentersList(),
  });

  const createMut = useMutation({
    mutationFn: () =>
      apiClient.costCentersCreate({
        name: form.name,
        description: form.description || null,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'cost-centers'],
      });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const updateMut = useMutation({
    mutationFn: () =>
      apiClient.costCentersUpdate(editing!.id, {
        name: form.name,
        description: form.description || null,
        active: form.active,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'cost-centers'],
      });
      closeModal();
    },
    onError: (err: ApiError) => setError(err.message),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => apiClient.costCentersDelete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['financial', 'cost-centers'],
      });
    },
  });

  function openCreate() {
    setEditing(null);
    setForm({ name: '', description: '', active: true });
    setError(null);
    setModalOpen(true);
  }
  function openEdit(cc: CostCenter) {
    setEditing(cc);
    setForm({
      name: cc.name,
      description: cc.description ?? '',
      active: cc.active,
    });
    setError(null);
    setModalOpen(true);
  }
  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (editing) updateMut.mutate();
    else createMut.mutate();
  }

  const items = listQ.data ?? [];

  return (
    <div className="space-y-3">
      <div className="flex justify-between">
        <p className="text-sm text-[var(--fg-muted)]">
          {items.length} centro(s) de custo
        </p>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Novo
        </button>
      </div>

      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[color-mix(in_oklch,var(--fg)_4%,transparent)] text-[var(--fg-muted)] text-xs uppercase">
            <tr>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Descrição</th>
              <th className="px-3 py-2 text-center">Ativo</th>
              <th className="px-3 py-2 w-20" />
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-8 text-center text-[var(--fg-muted)]"
                >
                  Nenhum centro de custo cadastrado.
                </td>
              </tr>
            )}
            {items.map((cc) => (
              <tr
                key={cc.id}
                className="border-t border-[var(--card-border)] hover:bg-[color-mix(in_oklch,var(--fg)_3%,transparent)]"
              >
                <td className="px-3 py-2 text-[var(--fg)]">{cc.name}</td>
                <td className="px-3 py-2 text-[var(--fg-muted)]">
                  {cc.description ?? '—'}
                </td>
                <td className="px-3 py-2 text-center">
                  {cc.active ? (
                    <span className="text-emerald-600">Sim</span>
                  ) : (
                    <span className="text-[var(--fg-muted)]">Não</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openEdit(cc)}
                    className="text-[var(--fg-muted)] hover:text-[var(--fg)] mr-2"
                  >
                    <Edit2 className="h-4 w-4 inline" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm(`Excluir centro "${cc.name}"?`))
                        deleteMut.mutate(cc.id);
                    }}
                    className="text-[var(--fg-muted)] hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <Modal
          title={editing ? 'Editar centro de custo' : 'Novo centro de custo'}
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}
            <Field label="Nome" required>
              <input
                type="text"
                value={form.name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, name: e.target.value }))
                }
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
                required
              />
            </Field>
            <Field label="Descrição">
              <textarea
                value={form.description}
                onChange={(e) =>
                  setForm((f) => ({ ...f, description: e.target.value }))
                }
                rows={3}
                className="w-full rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm"
              />
            </Field>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) =>
                  setForm((f) => ({ ...f, active: e.target.checked }))
                }
              />
              Ativo
            </label>
            <ModalFooter
              onCancel={closeModal}
              loading={createMut.isPending || updateMut.isPending}
            />
          </form>
        </Modal>
      )}
    </div>
  );
}

// ===========================================================================
// Componentes auxiliares
// ===========================================================================

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg shadow-xl w-full max-w-lg p-4">
        <div className="flex justify-between items-center mb-3 border-b border-[var(--card-border)] pb-2">
          <h2 className="text-lg font-semibold text-[var(--fg)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--fg-muted)] hover:text-[var(--fg)]"
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-[var(--fg-muted)] block mb-1">
        {label}
        {required && <span className="text-red-500"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ModalFooter({
  onCancel,
  loading,
}: {
  onCancel: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2 border-t border-[var(--card-border)]">
      <button
        type="button"
        onClick={onCancel}
        className="rounded border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-sm text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
      >
        Cancelar
      </button>
      <button
        type="submit"
        disabled={loading}
        className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-60"
      >
        {loading ? 'Salvando...' : 'Salvar'}
      </button>
    </div>
  );
}
